import { PlayCircle } from './PlayCircle';
import { TrackActionsWrapper } from '../Actions/TrackActions';
import { AlbumActionsWrapper } from '../Actions/AlbumActions';
import { ArtistActionsWrapper } from '../Actions/ArtistActions';
import { PlayistActionsWrapper } from '../Actions/PlaylistActions';

// Interfaces
import type { Track } from '../../interfaces/track';
import type { Album } from '../../interfaces/albums';
import type { Artist } from '../../interfaces/artist';
import type { Playlist } from '../../interfaces/playlists';

// Utils
import { useTranslation } from 'react-i18next';

// Redux
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { MIX_BUILDER_PLAYLIST_ID, MIX_BUILDER_ROUTE, MIX_BUILDER_URI } from '../../store/slices/yourLibrary';

// Constants
import { PLAYLIST_DEFAULT_IMAGE } from '../../constants/spotify';
import { uiActions } from '../../store/slices/ui';
import { useCallback, type MouseEvent } from 'react';
import { usePlayback } from '../../contexts/PlaybackContext';
import { useComposerIdSet } from '../../utils/useComposerIdSet';
import { ensureComposerKnown, getCatalogIdForSpotifyId } from '../../utils/composerRegistry';
import { extractSpotifyArtistId, resolveArtistRoute } from '../../utils/resolveArtistRoute';
import NavigableLink from '../shared/NavigableLink';
import '../Layout/components/Library/list/mixBuilderIcon.scss';

const isMixBuilderPlaylist = (playlist?: Playlist | null) =>
  playlist?.id === MIX_BUILDER_PLAYLIST_ID;

const Card = ({
  uri,
  title,
  image,
  rounded,
  description,
  context,
  linkProps,
  disablePlayback,
}: {
  uri: string;
  image: string;
  title: string;
  rounded?: boolean;
  description: string;
  context?: { context_uri?: string; uris?: string[] };
  linkProps: React.ComponentProps<typeof NavigableLink>;
  disablePlayback?: boolean;
}) => {
  const { playerState, isPlaying } = usePlayback();
  const currentContextUri = playerState?.context?.uri;
  const isCurrent =
    !disablePlayback && (currentContextUri === uri || currentContextUri === context?.context_uri);
  const isMixBuilder = uri === MIX_BUILDER_URI;

  const body = (
    <div
      style={{ cursor: 'pointer' }}
      className='playlist-card relative rounded-lg overflow-hidden  hover:bg-spotify-gray-lightest transition'
    >
      <div
        style={{ position: 'relative' }}
        className='aspect-square md:aspect-w-1 md:aspect-h-1/2 lg:aspect-w-1 lg:aspect-h-3/4 xl:aspect-w-1 xl:aspect-h-4/5 p-4'
      >
        {isMixBuilder ? (
          <div className={`mix-builder-icon ${rounded ? 'rounded' : ''}`} style={{ width: '100%', aspectRatio: '1' }} />
        ) : (
          <img
            src={image}
            alt={title}
            className={rounded ? 'rounded' : ''}
            style={{ borderRadius: 5, width: '100%' }}
          />
        )}
        {!disablePlayback ? (
          <div
            className={`circle-play-div transition translate-y-1/4 ${
              isCurrent && isPlaying ? 'active' : ''
            }`}
          >
            <PlayCircle image={image} isCurrent={isCurrent} context={context} />
          </div>
        ) : null}
      </div>
      <div className='playlist-card-info'>
        <h3 className='text-md font-semibold text-white'>{title}</h3>
        <p>{description}</p>
      </div>
    </div>
  );

  return (
    <NavigableLink {...linkProps}>
      {body}
    </NavigableLink>
  );
};

export const ArtistCard = ({
  item,
  onClick,
  getDescription,
}: {
  item: Artist;
  onClick?: () => void;
  getDescription?: (item: Artist) => string;
}) => {
  const navigate = useNavigate();
  const [t] = useTranslation(['artist']);
  const composerIds = useComposerIdSet();

  const title = item.name;
  const description = getDescription ? getDescription(item) : t('Artist');
  const defaultHref = resolveArtistRoute(item, composerIds);
  const handleClick = useCallback(
    async (event: MouseEvent<HTMLAnchorElement>) => {
      if (event.metaKey || event.altKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
        return;
      }
      event.preventDefault();
      onClick?.();
      const spotifyArtistId = extractSpotifyArtistId(item);
      if (spotifyArtistId) {
        const isComposer = await ensureComposerKnown(spotifyArtistId);
        if (isComposer) {
          const catalogId = getCatalogIdForSpotifyId(spotifyArtistId) ?? spotifyArtistId;
          navigate(`/composer/${catalogId}`);
          return;
        }
      }
      navigate(defaultHref);
    },
    [defaultHref, item, navigate, onClick]
  );

  return (
    <ArtistActionsWrapper artist={item} trigger={['contextMenu']}>
      <Card
        rounded
        title={title}
        uri={item.uri}
        description={description}
        image={item.images[0]?.url}
        context={{ context_uri: item.uri }}
        linkProps={{ to: defaultHref, onClick: handleClick }}
      />
    </ArtistActionsWrapper>
  );
};

export const AlbumCard = ({
  item,
  onClick,
  getDescription,
}: {
  item: Album;
  onClick?: () => void;
  getDescription?: (playlist: Album) => string;
}) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);

  const title = item.name;

  const description = getDescription
    ? getDescription(item)
    : item.artists
        .slice(0, 3)
        .map((artist) => artist.name)
        .join(', ');

  return (
    <AlbumActionsWrapper album={item} trigger={['contextMenu']}>
      <Card
        title={title}
        uri={item.uri}
        description={description}
        image={item.images[0]?.url}
        context={{ context_uri: item.uri }}
        linkProps={{
          to: `/album/${item.id}`,
          beforeNavigate: () => {
            if (!user) {
              dispatch(uiActions.openLoginModal(item.images[0].url));
              return false;
            }
            onClick?.();
            return true;
          },
        }}
      />
    </AlbumActionsWrapper>
  );
};

export const PlaylistCard = ({
  item,
  onClick,
  getDescription,
}: {
  item: Playlist;
  onClick?: () => void;
  getDescription?: (playlist: Playlist) => string;
}) => {
  const navigate = useNavigate();
  const [t] = useTranslation(['playlist']);

  const title = item.name;
  const isMixBuilder = isMixBuilderPlaylist(item);
  const image = item.images && item.images.length ? item.images[0].url : PLAYLIST_DEFAULT_IMAGE;
  const description = isMixBuilder
    ? 'Build custom mixes'
    : getDescription
    ? getDescription(item)
    : item.tracks?.total + ' ' + t(item.tracks?.total === 1 ? 'song' : 'songs');

  if (isMixBuilder) {
    return (
      <Card
        title={title}
        uri={item.uri}
        description={description}
        image={image}
        disablePlayback
        linkProps={{
          to: MIX_BUILDER_ROUTE,
          onClick: (e) => {
            if (e.metaKey || e.altKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
            e.preventDefault();
            onClick?.();
            navigate(MIX_BUILDER_ROUTE);
          },
        }}
      />
    );
  }

  return (
    <PlayistActionsWrapper playlist={item} trigger={['contextMenu']}>
      <Card
        title={title}
        uri={item.uri}
        description={description}
        context={{ context_uri: item.uri }}
        linkProps={{
          to: `/playlist/${item.id}`,
          onClick: (e) => {
            if (e.metaKey || e.altKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
            e.preventDefault();
            onClick?.();
            navigate(`/playlist/${item.id}`);
          },
        }}
        image={image}
      />
    </PlayistActionsWrapper>
  );
};

export const TrackCard = ({
  item,
  getDescription,
  onClick,
}: {
  item: Track;
  onClick?: () => void;
  getDescription?: (track: Track) => string;
}) => {
  const navigate = useNavigate();
  const description = getDescription ? getDescription(item) : item.album.name;

  return (
    <TrackActionsWrapper track={item} trigger={['contextMenu']}>
      <Card
        uri={item.uri}
        title={item.name}
        description={description}
        context={{ uris: [item.uri] }}
        image={item.album.images[0]?.url}
        linkProps={{
          to: `/album/${item.album.id}`,
          onClick: (e) => {
            if (e.metaKey || e.altKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
            e.preventDefault();
            onClick?.();
            navigate(`/album/${item.album.id}`);
          },
        }}
      />
    </TrackActionsWrapper>
  );
};
